import React from 'react';
import { Satellite, Zap, Globe, Lock, CheckCircle2, ArrowRight, AlertCircle, Database, Settings } from 'lucide-react';

export const RinexPageContent: React.FC = () => {
  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center gap-3 mb-4">
          <Satellite className="w-8 h-8 text-cyan-400" />
          <h2 className="text-3xl font-bold text-cyan-400">RINEX Agent</h2>
        </div>
        <p className="text-xl text-gray-300 mb-4">
          Process RINEX (Receiver Independent Exchange Format) files to generate high-precision GNSS solutions.
        </p>
        <p className="text-gray-400">
          RINEX is the international standard format for raw GNSS (Global Navigation Satellite System) observations. 
          It contains unprocessed data from satellites—GPS, GLONASS, Galileo, BeiDou, and others. The RINEX Agent 
          takes these raw observations and transforms them into precise coordinate solutions using advanced algorithms.
        </p>
      </section>

      <section>
        <h3 className="text-2xl font-bold text-indigo-300 mb-4">What RINEX Does</h3>
        <p className="text-gray-300 mb-4">
          RINEX files contain satellite observation data: signal arrival times, pseudoranges, Doppler frequencies, 
          and signal strength indicators. This raw data is the foundation for precise positioning, but it requires 
          sophisticated processing to convert into usable coordinates.
        </p>
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 space-y-4">
          <div className="flex gap-4">
            <Satellite className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-gray-100 mb-1">Multi-Constellation Support</h4>
              <p className="text-gray-400">Process data from GPS, GLONASS, Galileo, BeiDou, QZSS, and NavIC satellites simultaneously</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Zap className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-gray-100 mb-1">Advanced Algorithms</h4>
              <p className="text-gray-400">SPP (Single Point Positioning), RTK (Real-Time Kinematic), and PPP (Precise Point Positioning) modes</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Database className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-gray-100 mb-1">Quality Metrics</h4>
              <p className="text-gray-400">DOP calculations, residual analysis, and solution quality indicators</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Settings className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-gray-100 mb-1">Flexible Output</h4>
              <p className="text-gray-400">Export results in LLH (Latitude/Longitude/Height), XYZ (Cartesian), or ENU (East/North/Up) formats</p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold text-purple-300 mb-4">Processing Modes</h3>
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg p-5">
            <h4 className="font-semibold text-cyan-300 mb-2">Single Point Positioning (SPP)</h4>
            <p className="text-gray-400 text-sm mb-3">
              Standalone positioning using signals from a single receiver. Uses least-squares optimization 
              to find the best-fit position based on satellite observations.
            </p>
            <ul className="space-y-1 text-sm text-gray-400 ml-4">
              <li>✓ Typical Accuracy: 5-10 meters</li>
              <li>✓ Processing Time: Fast (real-time capable)</li>
              <li>✓ Requirements: Single RINEX file</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-5">
            <h4 className="font-semibold text-purple-300 mb-2">Real-Time Kinematic (RTK)</h4>
            <p className="text-gray-400 text-sm mb-3">
              Relative positioning using observations from a base station. The receiver position is determined 
              relative to a known reference point, dramatically improving accuracy.
            </p>
            <ul className="space-y-1 text-sm text-gray-400 ml-4">
              <li>✓ Typical Accuracy: 2-5 cm</li>
              <li>✓ Processing Time: Real-time</li>
              <li>✓ Requirements: Reference station data + rover data</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-5">
            <h4 className="font-semibold text-green-300 mb-2">Precise Point Positioning (PPP)</h4>
            <p className="text-gray-400 text-sm mb-3">
              Advanced technique using precise satellite ephemerides and clock corrections. Achieves high accuracy 
              without requiring a base station, but needs longer observation periods.
            </p>
            <ul className="space-y-1 text-sm text-gray-400 ml-4">
              <li>✓ Typical Accuracy: 10-30 cm</li>
              <li>✓ Processing Time: Post-processing (30+ minutes typical)</li>
              <li>✓ Requirements: Single RINEX file (longer observation window)</li>
            </ul>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold text-yellow-300 mb-4">Use Cases</h3>
        <ul className="space-y-3">
          <li className="flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">Survey Control Networks:</strong> Establish reference points with centimeter accuracy using PPP
            </div>
          </li>
          <li className="flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">RTK Stakeout:</strong> Real-time guidance for construction equipment positioning
            </div>
          </li>
          <li className="flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">Drone Surveys:</strong> Process GNSS data from UAV flights for precise georeferencing
            </div>
          </li>
          <li className="flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">Boundary Verification:</strong> Validate coordinate extraction with high-precision GNSS
            </div>
          </li>
          <li className="flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">Deformation Monitoring:</strong> Track changes in precise coordinates over time
            </div>
          </li>
          <li className="flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">Quality Control:</strong> Verify field survey accuracy against GNSS solutions
            </div>
          </li>
        </ul>
      </section>

      <section>
        <h3 className="text-2xl font-bold text-blue-300 mb-4">Technical Capabilities</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-100 mb-3">Input Support</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>✓ RINEX 2.11 Format</li>
              <li>✓ RINEX 3.03 Format</li>
              <li>✓ Compressed (.gz, .Z)</li>
              <li>✓ Hatanaka Compressed (.rnx.gz)</li>
              <li>✓ Multiple file extensions</li>
            </ul>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-100 mb-3">Output Formats</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>✓ CSV (comma-separated values)</li>
              <li>✓ GeoJSON (for mapping)</li>
              <li>✓ KML (Google Earth compatible)</li>
              <li>✓ JSON (structured data)</li>
              <li>✓ Raw GNSS solutions</li>
            </ul>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-100 mb-3">Analysis Features</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>✓ DOP (Dilution of Precision)</li>
              <li>✓ Satellite visibility analysis</li>
              <li>✓ Signal strength metrics</li>
              <li>✓ Pseudorange residuals</li>
              <li>✓ Solution convergence</li>
            </ul>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-100 mb-3">Coordinate Systems</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>✓ WGS84 (ITRS)</li>
              <li>✓ Various projections</li>
              <li>✓ Custom datum support</li>
              <li>✓ Height systems (ellipsoidal/orthometric)</li>
              <li>✓ Local coordinate frames</li>
            </ul>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold text-red-300 mb-4">Getting Started</h3>
        <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg p-6 space-y-4">
          <div className="flex gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-500/20 flex-shrink-0">
              <span className="font-bold text-cyan-400">1</span>
            </div>
            <div>
              <h4 className="font-semibold text-gray-100 mb-1">Get API Access</h4>
              <p className="text-gray-400 text-sm">
                Provide your own API key or use the secret password to enable RINEX Agent features
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-500/20 flex-shrink-0">
              <span className="font-bold text-cyan-400">2</span>
            </div>
            <div>
              <h4 className="font-semibold text-gray-100 mb-1">Select Processing Mode</h4>
              <p className="text-gray-400 text-sm">
                Choose between SPP, RTK, or PPP based on your accuracy requirements
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-500/20 flex-shrink-0">
              <span className="font-bold text-cyan-400">3</span>
            </div>
            <div>
              <h4 className="font-semibold text-gray-100 mb-1">Upload RINEX Files</h4>
              <p className="text-gray-400 text-sm">
                Upload your observation file (and optional reference station data for RTK)
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-500/20 flex-shrink-0">
              <span className="font-bold text-cyan-400">4</span>
            </div>
            <div>
              <h4 className="font-semibold text-gray-100 mb-1">Configure Parameters</h4>
              <p className="text-gray-400 text-sm">
                Select constellations, output format, and other processing parameters
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-500/20 flex-shrink-0">
              <span className="font-bold text-cyan-400">5</span>
            </div>
            <div>
              <h4 className="font-semibold text-gray-100 mb-1">Download Results</h4>
              <p className="text-gray-400 text-sm">
                Get precise coordinates in your preferred format (CSV, GeoJSON, KML, etc.)
              </p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold text-green-300 mb-4">Why Use RINEX Agent</h3>
        <ul className="space-y-3">
          <li className="flex gap-3">
            <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">No Software Installation:</strong> Process RINEX files directly in your browser
            </div>
          </li>
          <li className="flex gap-3">
            <Globe className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">Multi-Constellation Support:</strong> Leverage all available satellite systems
            </div>
          </li>
          <li className="flex gap-3">
            <Lock className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">Secure & Private:</strong> All processing happens client-side; data never leaves your browser
            </div>
          </li>
          <li className="flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">Production Ready:</strong> Industry-standard algorithms validated by professional surveyors
            </div>
          </li>
          <li className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-100">Flexible Output:</strong> Export to any format your downstream tools require
            </div>
          </li>
        </ul>
      </section>

      <section>
        <h3 className="text-2xl font-bold text-orange-300 mb-4">Advanced Features</h3>
        <div className="space-y-4">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-100 mb-2">Constellation Configuration</h4>
            <p className="text-gray-400 text-sm">
              Process any combination of GPS, GLONASS, Galileo, BeiDou, QZSS, and NavIC satellites. 
              Exclude problematic constellations or test impact of individual systems.
            </p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-100 mb-2">Quality Assurance</h4>
            <p className="text-gray-400 text-sm">
              Automatic validation of input files, detection of invalid observations, and quality indicators 
              for results. Know exactly how confident your solutions are.
            </p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-100 mb-2">Job History</h4>
            <p className="text-gray-400 text-sm">
              Track all processing jobs with timestamps, parameters, and results. Reproduce past analyses 
              or compare different processing strategies.
            </p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-100 mb-2">Simulation Mode</h4>
            <p className="text-gray-400 text-sm">
              Test your workflows without consuming API quota. Perfect for learning, testing parameters, 
              or demonstrating the platform.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold text-indigo-300 mb-4">Understanding Your Results</h3>
        <p className="text-gray-400 mb-4">
          The RINEX Agent returns not just coordinates, but comprehensive analysis including:
        </p>
        <ul className="space-y-3 text-gray-400 text-sm">
          <li>
            <strong className="text-gray-100">Coordinate Solutions:</strong> Latitude, longitude, height in your chosen format
          </li>
          <li>
            <strong className="text-gray-100">Accuracy Estimates:</strong> Standard deviations showing confidence in each coordinate
          </li>
          <li>
            <strong className="text-gray-100">DOP Metrics:</strong> PDOP, GDOP, HDOP showing satellite geometry quality
          </li>
          <li>
            <strong className="text-gray-100">Satellite Count:</strong> Number of satellites used at each epoch
          </li>
          <li>
            <strong className="text-gray-100">Residuals:</strong> Fit quality between observation and computed values
          </li>
          <li>
            <strong className="text-gray-100">Convergence Status:</strong> How long processing took and convergence quality
          </li>
        </ul>
      </section>

      <section className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg p-8 text-center">
        <h3 className="text-2xl font-bold text-cyan-300 mb-3">Ready to Process RINEX Files?</h3>
        <p className="text-gray-300 mb-6">
          Access the RINEX Agent through the LandSurv.ai main application. Provide an API key 
          or use the development bypass to start processing your GNSS data.
        </p>
        <a
          href="https://landsurv.ai"
          className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 text-gray-900 font-semibold rounded-lg hover:bg-cyan-400 transition"
        >
          Launch LandSurv.ai
          <ArrowRight className="w-5 h-5" />
        </a>
      </section>
    </div>
  );
};
